import React, { useEffect, useMemo, useRef } from 'react'
import { Box } from '@mui/material'
import * as d3 from 'd3'

import { INTERAX_COLORS } from '@/constants/colors'
import type { LoopCommunication } from './StackedLineChartCenteredVisualization'
import { transformLoopsToFunnelData } from './funnelGraphDataTransformer'

export interface SingleLoopBarChartProps {
  loops: LoopCommunication[]
  height?: number
  loopLabels?: string[]
  direction?: 'horizontal' | 'vertical'
}

const BASE_MARGIN = {
  top: 80, // Space for loop label and total
  right: 5,
  bottom: 40,
  left: 60, // Space for player names if needed
  text: {
    left: 10,
    top: 0
  }
}

const SingleLoopBarChart: React.FC<SingleLoopBarChartProps> = ({ loops, height = 320, loopLabels }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)

  // Transform data for single loop
  const chartData = useMemo(() => {
    if (loops.length !== 1) {
      return null
    }

    const graphData = transformLoopsToFunnelData(loops, { loopLabels })

    if (!graphData || !graphData.subLabels || !graphData.values || graphData.values.length === 0) {
      return null
    }

    const loop = loops[0]
    // Use the actual loopIndex from the loop data, or fall back to 1-based index
    // If loopLabels prop is provided, use that; otherwise use the loop's actual index
    const loopLabel =
      loopLabels && loopLabels[0]
        ? loopLabels[0]
        : graphData.labels[0] ||
          (() => {
            const actualLoopIndex = loop.loopIndex && loop.loopIndex > 0 ? loop.loopIndex : 1
            return `Loop ${actualLoopIndex}`
          })()
    const playerValues = graphData.values[0] // First (and only) loop's values
    const total = playerValues.reduce((sum, val) => sum + val, 0)

    // Create player data with colors in the same order as funnel graph
    // Don't sort - maintain original order to match funnel behavior and keep legend consistent
    const players = graphData.subLabels.map((playerName, index) => ({
      name: playerName,
      value: playerValues[index] || 0,
      color: Array.isArray(graphData.colors[index])
        ? (graphData.colors[index] as string[])[0]
        : typeof graphData.colors[index] === 'string'
          ? graphData.colors[index]
          : INTERAX_COLORS.white
    }))

    return {
      loopLabel,
      total,
      players
    }
  }, [loops, loopLabels])

  // Extract legend items (same structure as funnel)
  const legendItems = useMemo(() => {
    if (!chartData) {
      return []
    }

    return chartData.players.map((player) => ({
      name: player.name,
      color: player.color
    }))
  }, [chartData])

  useEffect(() => {
    if (!containerRef.current || !chartData) {
      return
    }

    const container = containerRef.current
    const width = container.clientWidth
    const chartHeight = height

    // Clear previous SVG
    d3.select(container).select('svg').remove()

    // Create SVG
    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', chartHeight)
      .attr('style', 'font-family: Lexend Deca, system-ui, sans-serif')

    const g = svg.append('g').attr('transform', `translate(${BASE_MARGIN.left},${BASE_MARGIN.top})`)

    // Calculate chart dimensions
    const chartWidth = width - BASE_MARGIN.left - BASE_MARGIN.right
    const chartAreaHeight = chartHeight - BASE_MARGIN.top - BASE_MARGIN.bottom

    // Create scales
    const maxValue = Math.max(...chartData.players.map((p) => p.value), 1)
    const xScale = d3.scaleLinear().domain([0, maxValue]).range([0, chartWidth]).nice()

    const yScale = d3
      .scaleBand()
      .domain(chartData.players.map((_, i) => String(i)))
      .range([0, chartAreaHeight])
      .paddingInner(0.1)
      .paddingOuter(0.1)

    // Add loop label and total at the top
    const labelGroup = g.append('g').attr('class', 'loop-label-group')

    // Total value (large)
    labelGroup
      .append('text')
      .attr('class', 'loop-total')
      .attr('x', chartWidth / 2)
      .attr('y', -50)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', INTERAX_COLORS.white)
      .attr('font-size', '24px')
      .attr('font-weight', '600')
      .text(String(chartData.total))

    // Loop label (small, green)
    labelGroup
      .append('text')
      .attr('class', 'loop-label')
      .attr('x', chartWidth / 2)
      .attr('y', -30)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', INTERAX_COLORS.green)
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .text(chartData.loopLabel)

    // Create bars
    const bars = g
      .selectAll('.bar')
      .data(chartData.players)
      .enter()
      .append('g')
      .attr('class', 'bar-group')
      .attr('transform', (_, i) => `translate(0,${yScale(String(i))})`)

    // Add bar rectangles
    bars
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', (d) => xScale(d.value))
      .attr('height', yScale.bandwidth())
      .attr('fill', (d) => d.color)
      .attr('rx', 2)
      .attr('ry', 2)
      .style('cursor', 'pointer')
      .on('mouseenter', function (event, d) {
        d3.select(this).attr('opacity', 0.8)
        // Show tooltip
        const tooltip = d3
          .select('body')
          .append('div')
          .attr('class', 'd3-funnel-js-tooltip')
          .style('position', 'absolute')
          .style('background-color', INTERAX_COLORS.offBlack)
          .style('border', `1px solid ${INTERAX_COLORS.white25}`)
          .style('color', INTERAX_COLORS.white)
          .style('padding', '8px 12px')
          .style('border-radius', '4px')
          .style('pointer-events', 'none')
          .style('z-index', '9999')
          .style('font-family', 'Lexend Deca, system-ui, sans-serif')
          .style('font-size', '12px')
          .style('opacity', 0)

        tooltip
          .html(`${d.name}: ${Math.round(d.value)}`)
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY - 10}px`)
          .transition()
          .duration(200)
          .style('opacity', 1)
      })
      .on('mousemove', function (event) {
        const tooltip = d3.select('.d3-funnel-js-tooltip')
        tooltip.style('left', `${event.pageX + 10}px`).style('top', `${event.pageY - 10}px`)
      })
      .on('mouseleave', function () {
        d3.select(this).attr('opacity', 1)
        d3.select('.d3-funnel-js-tooltip').remove()
      })

    // Add value labels on bars (with rounded gray background)
    bars
      .append('g')
      .attr('class', 'bar-label-group')
      .each(function (d) {
        const labelGroup = d3.select(this)
        const barWidth = xScale(d.value)
        const barHeight = yScale.bandwidth()
        const labelX = barWidth / 2
        const labelY = barHeight / 2

        // Background rectangle
        const textPadding = 6
        const textHeight = 14
        const rectWidth = 35
        const rectHeight = textHeight + textPadding

        labelGroup
          .append('rect')
          .attr('class', 'bar-label-bg')
          .attr('x', labelX - rectWidth / 2)
          .attr('y', labelY - rectHeight / 2)
          .attr('width', rectWidth)
          .attr('height', rectHeight)
          .attr('rx', 4)
          .attr('ry', 4)
          .attr('fill', 'rgba(0, 0, 0, 0.6)')

        // Value text
        labelGroup
          .append('text')
          .attr('class', 'bar-label-text')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('fill', INTERAX_COLORS.white)
          .attr('font-size', '11px')
          .attr('font-weight', '500')
          .attr('font-family', 'Lexend Deca, system-ui, sans-serif')
          .attr('pointer-events', 'none')
          .text(String(Math.round(d.value)))
      })

    // Store SVG reference for cleanup
    svgRef.current = svg.node() as SVGSVGElement

    // Handle window resize for better responsiveness
    const handleResize = () => {
      if (!container || !chartData) return

      const newWidth = container.clientWidth
      if (newWidth === 0) return

      // Update SVG width
      svg.attr('width', newWidth)

      // Recalculate chart dimensions
      const newChartWidth = newWidth - BASE_MARGIN.left - BASE_MARGIN.right
      xScale.range([0, newChartWidth])

      // Update bars
      bars.select('.bar').attr('width', (d) => xScale(d.value))

      // Update label positions
      bars.each(function (d) {
        const barData = d as { name: string; value: number; color: string }
        const labelGroup = d3.select(this).select('.bar-label-group')
        const barWidth = xScale(barData.value)
        const labelX = barWidth / 2

        labelGroup.select('.bar-label-bg').attr('x', labelX - 17.5) // rectWidth / 2
        labelGroup.select('.bar-label-text').attr('x', labelX)
      })

      // Update loop label position
      labelGroup.select('.loop-total').attr('x', newChartWidth / 2)
      labelGroup.select('.loop-label').attr('x', newChartWidth / 2)
    }

    const resizeObserver = new ResizeObserver(handleResize)
    if (container) {
      resizeObserver.observe(container)
    }

    // Cleanup function
    return () => {
      resizeObserver.disconnect()
      d3.select(container).select('svg').remove()
      d3.select('.d3-funnel-js-tooltip').remove()
    }
  }, [chartData, height])

  if (!chartData) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      {/* Legend: Color and Player Name - Compact (same as funnel) */}
      {legendItems.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            paddingY: 0.5,
            paddingLeft: `${BASE_MARGIN.left}px` // Align legend with graph start position
          }}
        >
          {legendItems.map((item, index) => (
            <Box
              key={`legend-${item.name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  backgroundColor: item.color,
                  flexShrink: 0
                }}
              />
              <Box
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  color: INTERAX_COLORS.white,
                  fontFamily: 'Lexend Deca, system-ui, sans-serif',
                  lineHeight: 1
                }}
              >
                {item.name}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height,
          position: 'relative',
          '& svg': {
            overflow: 'visible'
          }
        }}
      />
    </Box>
  )
}

export default SingleLoopBarChart
